import { HttpParams } from '@angular/common/http';
import { Inject } from '@angular/core';
import { ENV, IEnvironment } from 'env/config';

import { Point, Polygon } from 'ol/geom';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Dropdown, IPage, TDropdown } from '../interfaces/helpers';
import {
  Endpoint,
  PageConfig,
  PAGES,
  ResultGeoServer,
} from '../interfaces/internal';

import {
  IGeoServer,
  IGeoServerCity,
  IGeoServerCityFeatures,
  IGeoServerCityFeaturesProperties,
} from '../interfaces/models';
import { ITerritory, Territory } from '../models/territory.model';

import { ITerritoryFsa, Fsa } from '../models/fsa.model';
import { Result } from '../interfaces/helpers';
import { Page } from './page';
import { PageTitles } from '../interfaces/types';
import { TerritoryService } from '../services/territory';
import { FsaService } from '../services/fsa';

export class FsaPage extends Page<Fsa> {
  private header;

  constructor(
    @Inject(ENV) env: IEnvironment,
    { endpoint }: Endpoint,
    config: PageConfig,
    private territoryFsa: FsaService,
    private territory: TerritoryService
  ) {
    super(endpoint as string, PageTitles.fsa, config);

    this.initializeColumns({
      keys: new Fsa(),
      editableFields: [],
      dropdownFields: [],
      hiddenTableFields: ['point', 'geometry', 'checked'],
    });

    this.header = env.api.headers.k2view;
  }

  get actions(): string[] {
    return [];
  }

  get hiddenFields(): string[] {
    return ['checked'];
  }

  fetch(queryString?): Observable<Result<Fsa>> {
    if (!queryString) {
      return of({
        items: [],
        meta: null,
        links: null,
        source: null,
      });
    }

    const newQueryString = this.fsaQuery(`attr15 = '${queryString?.coid}'`);

    let listFsas: ITerritoryFsa[] = [];

    return this.territoryFsa.getAll(queryString).pipe(
      take(1),
      switchMap((result) => {
        const fsas = result.items;
        listFsas = [...fsas] as any;
        const territoryIds = [
          ...new Set((fsas as any).map((f) => f.territoryId)),
        ];

        return territoryIds.length > 0
          ? this.territory.getAll({ ids: territoryIds.join() })
          : of({ items: [], links: null, meta: null });
      }),
      switchMap((result) =>
        this.queryData(newQueryString, listFsas, result.items)
      ),
      take(1),
      tap(({ items }) => {
        this.pageItems.next(items);
      })
    );
  }

  fetchDropdownData(): Observable<Dropdown[]> {
    const queryString = {
      service: 'WFS',
      request: 'GetFeature',
      version: '2.0.0',
      typeName: 'telus:telus_can_bndy_wc_a',
      outputformat: 'application/json',
      propertyName: 'attr_15,province',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cql_filter: `province in ('AB', 'BC', 'QC')`,
    };

    return this.dataService
      .get<ResultGeoServer>(this.url, queryString, this.header, true)
      .pipe(
        switchMap((result) => {
          let items: TDropdown[] = [];

          if (!result || !result.features) {
            return of(items);
          }
          const features = result.features as IGeoServer[];
          const feats = features
            .filter(
              (x) =>
                x.properties.province === 'AB' ||
                x.properties.province === 'BC' ||
                x.properties.province === 'QC'
            )
            .sort((a, b) =>
              a.properties.attr_15 > b.properties.attr_15 ? 1 : -1
            );

          items = feats.map((x) => ({
            text: x.properties.attr_15,
            value: x.properties.attr_15,
            checked: false,
          }));

          return of(items);
        }),
        take(1)
      );
  }

  fetchFSAs(territory: Territory): Observable<Fsa[]> {
    return this.territoryFsa
      .getByTerritory({ territoryId: territory.id }, { territory })
      .pipe(
        take(1),
        map(({ items }) => items)
      );
  }

  fetchFilterData(queryString?): Observable<Fsa[]> {
    const fsas = queryString.fsas as string[];

    if (queryString.fsas.length === 0) {
      return of([]);
    }

    const newQueryString = this.fsaQuery(
      `attr11 in (${fsas.map((f) => `'${f}'`).join()}) and attr15 = '${
        queryString.coid
      }'`
    );

    return this.queryData(newQueryString).pipe(map(({ items }) => items));
  }

  fetchById(id: string): Observable<Fsa> {
    return this.getItem(id, '', this.header, true).pipe(
      map((data: IGeoServer) => {
        const {
          attr11,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          premise_sfu_cnt,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          premise_sxu_cnt,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          premise_mdu_cnt,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          premise_mxu_cnt,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          premise_other_cnt,
        } = data.properties;

        return new Fsa({
          id,
          name: attr11,
          addressNumber:
            premise_sfu_cnt +
            premise_sxu_cnt +
            premise_mdu_cnt +
            premise_mxu_cnt +
            premise_other_cnt,
          sfu: premise_sfu_cnt,
          sxu: premise_sxu_cnt,
          mdu: premise_mdu_cnt,
          mxu: premise_mxu_cnt,
          others: premise_other_cnt,
          geometry: new Polygon(data.geometry.coordinates),
          point: new Point(data.geometry.coordinates),
          territory: '',
          checked: false,
        });
      })
    );
  }

  fetchByCityFSAs(fsas: string[], territoryId: string): Observable<Fsa[]> {
    const formattedFsas = fsas.map((fsa) => `'${fsa}'`).join(',');

    const newQueryString = this.citiesFsaByFSAQuery(formattedFsas);

    const xfsas: Fsa[] = [];
    const listFsas: ITerritoryFsa[] = [];

    const params = new HttpParams({
      fromObject: newQueryString,
    });

    const header = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/x-www-form-urlencoded',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: '*/*',
    };
    return this.getItemsPost<ResultGeoServer>(params, header).pipe(
      map((result: any) => {
        const features = result.items;
        const data: IGeoServer[] = features as IGeoServer[];

        for (const item of data) {
          const fsa = new Fsa();

          fsa.id = item.properties.cfsauid;
          fsa.name = item.properties.cfsauid;
          fsa.addressNumber =
            item.properties.premise_sfu_cnt +
            item.properties.premise_sxu_cnt +
            item.properties.premise_mdu_cnt +
            item.properties.premise_mxu_cnt +
            item.properties.premise_other_cnt;
          fsa.sfu = item.properties.premise_sfu_cnt;
          fsa.sxu = item.properties.premise_sxu_cnt;
          fsa.mdu = item.properties.premise_mdu_cnt;
          fsa.mxu = item.properties.premise_mxu_cnt;

          fsa.others = item.properties.premise_other_cnt;
          fsa.geometry = new Polygon(item.geometry.coordinates);
          fsa.point = new Point(item.geometry.coordinates);
          fsa.territory = '';
          fsa.checked = false;

          if (fsas.filter((x) => x === item.properties.cfsauid).length > 0) {
            xfsas.push(fsa);
          }
        }

        return xfsas;
      }),
      take(1),
      tap((fsa) => {
        this.pageItems.next(fsa);
      })
    );
  }

  update(item: Fsa): Observable<Fsa> {
    let updatedFsas: Fsa[];
    return this.items.pipe(
      take(1),
      switchMap((fsas) => {
        if (!fsas || fsas.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(fsas);
        }
      }),
      switchMap((fsas) => {
        const fsaIndex = fsas.findIndex((i) => i.id === item.id);
        updatedFsas = [...fsas];
        const oldFsa = updatedFsas[fsaIndex];
        updatedFsas[fsaIndex] = new Fsa({ ...item, id: oldFsa.id });
        return this.updateItems(updatedFsas[fsaIndex]);
      }),
      tap(() => {
        this.pageItems.next(updatedFsas);
      })
    );
  }

  fetchCities(): Observable<IGeoServerCityFeaturesProperties[]> {
    const newQueryString = this.cityQuery();

    return this.getItems<IGeoServerCity>(
      newQueryString,
      this.header,
      true
    ).pipe(
      map((res: any) => {
        const features = res.items;
        const result = [...(features as IGeoServerCityFeatures[])];

        const cities = result.map((x) => ({
          prname: x.properties.prname,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          city_name: x.properties.city_name,
          id: x.properties.prname,
        }));

        const filteredArr = cities.reduce((thing, current) => {
          const x = thing.find((item) => item.city_name === current.city_name);
          if (!x) {
            return thing.concat([current]);
          } else {
            return thing;
          }
        }, []);

        return filteredArr.sort((a, b) => (a.city_name > b.city_name ? 1 : -1));
      })
    );
  }

  createFromDTO(item): Fsa {
    return item;
  }

  private fsaQuery(cqlFilter: string) {
    return {
      service: 'WFS',
      request: 'GetFeature',
      version: '2.0.0',
      typeName: 'telus:fdsa_polygon',
      outputformat: 'application/json',
      sortBy: 'attr11',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cql_filter: cqlFilter,
    };
  }

  private cityQuery() {
    return {
      service: 'WFS',
      request: 'GetFeature',
      version: '2.0.0',
      typeName: 'telus:postal_fsa_boundaries',
      outputformat: 'application/json',
      propertyname: 'city_name,prname',
    };
  }

  private cityFSAQuery(city: string) {
    return {
      service: 'WFS',
      request: 'GetFeature',
      version: '2.0.0',
      typeName: 'telus:postal_fsa_boundaries',
      outputformat: 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cql_filter: `city_name='${city}'`,
    };
  }

  private citiesFsaByFSAQuery(fsa: string) {
    return {
      service: 'WFS',
      request: 'GetFeature',
      version: '2.0.0',
      typeName: 'telus:postal_fsa_boundaries',
      outputformat: 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cql_filter: `cfsauid IN (${fsa})`,
    };
  }

  private queryData(
    queryString,
    fsas?: ITerritoryFsa[],
    territories?: ITerritory[]
  ): Observable<Result<Fsa>> {
    const params = new HttpParams({
      fromObject: queryString,
    });
    const header = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/x-www-form-urlencoded',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      Accept: '*/*',
    };
    return this.getItemsPost<ResultGeoServer>(params, header).pipe(
      map((result: any) => {
        const features = result.items as IGeoServer[];
        const items: Fsa[] = [];

        (features as IGeoServer[]).forEach((record) => {
          const {
            attr11,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            premise_sfu_cnt,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            premise_sxu_cnt,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            premise_mdu_cnt,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            premise_mxu_cnt,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            premise_other_cnt,
          } = record.properties;

          let indexTerritory = -1;
          if (fsas && fsas.length > 0) {
            const fas = fsas.filter((f) => f.fsa === attr11);
            if (fas.length > 0) {
              indexTerritory = territories.findIndex(
                (t) => t.id === fas[0].territoryId
              );
            }
          }

          items.push(
            new Fsa({
              id: attr11,
              name: attr11,
              addressNumber:
                premise_sfu_cnt +
                premise_sxu_cnt +
                premise_mdu_cnt +
                premise_mxu_cnt +
                premise_other_cnt,
              sfu: premise_sfu_cnt,
              sxu: premise_sxu_cnt,
              mdu: premise_mdu_cnt,
              mxu: premise_mxu_cnt,
              others: premise_other_cnt,
              geometry: new Polygon(
                record.geometry.coordinates.map((i) =>
                  i.map((c) => [c[1], c[0]])
                )
              ),
              point: new Point(
                record.geometry.coordinates.map((i) =>
                  i.map((j) => j.map((k) => [k[1], k[0]]))
                )
              ),
              territory:
                indexTerritory > -1
                  ? territories[indexTerritory].territoryName
                  : '',
              checked: false,
            })
          );
        });

        return {
          links: result.links,
          meta: result.meta,
          items,
        };
      })
    );
  }
}
